import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Trash2, LogOut, MessageSquare, UserPlus, Users, Key } from "lucide-react";
import ApiKeysPanel from "./ApiKeysPanel";

export default function AdminDashboard({ backendUrl }) {
  const [signups, setSignups] = useState([]);
  const [comments, setComments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("subscribers");
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [addDays, setAddDays] = useState("30");
  const [addMsg, setAddMsg] = useState("");
  const [addLink, setAddLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [passcodes, setPasscodes] = useState([]);
  const [passcodeUsers, setPasscodeUsers] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newCodeDays, setNewCodeDays] = useState("14");
  const [codeMsg, setCodeMsg] = useState("");
  const navigate = useNavigate();

  const getAuthConfig = () => {
    const token = sessionStorage.getItem("pord_admin_token");
    const config = { withCredentials: true };
    if (token) config.headers = { Authorization: `Bearer ${token}` };
    return config;
  };

  const fetchData = async () => {
    try {
      const config = getAuthConfig();
      const [signupsRes, statsRes, commentsRes, subsRes, passRes, puRes] = await Promise.all([
        axios.get(`${backendUrl}/api/admin/signups`, config),
        axios.get(`${backendUrl}/api/admin/stats`, config),
        axios.get(`${backendUrl}/api/admin/comments`, config),
        axios.get(`${backendUrl}/api/admin/subscribers`, config),
        axios.get(`${backendUrl}/api/admin/passcodes`, config).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/admin/passcode-users`, config).catch(() => ({ data: [] })),
      ]);
      setSignups(signupsRes.data);
      setStats(statsRes.data);
      setComments(commentsRes.data);
      setSubscribers(subsRes.data);
      setPasscodes(passRes.data);
      setPasscodeUsers(puRes.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        sessionStorage.removeItem("pord_admin_token");
        navigate("/admin");
      } else {
        setError("Failed to load data");
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${backendUrl}/api/admin/signups/${id}`, getAuthConfig());
      fetchData();
    } catch {}
  };

  const handleDeactivate = async (id) => {
    try {
      await axios.delete(`${backendUrl}/api/admin/subscribers/${id}`, getAuthConfig());
      fetchData();
    } catch {}
  };

  const handleAddSubscriber = async () => {
    if (!addEmail) return;
    try {
      const { data } = await axios.post(`${backendUrl}/api/admin/subscribers`, { email: addEmail, name: addName, days: addDays ? parseInt(addDays) : null, origin_url: window.location.origin.replace('/admin', '') }, getAuthConfig());
      let msg = data.message || `Updated: ${addEmail}`;
      setAddMsg(msg);
      setAddLink(data.invite_link || "");
      setAddEmail(""); setAddName(""); setAddDays("30");
      fetchData();
      setTimeout(() => { setAddMsg(""); setAddLink(""); }, 60000);
    } catch (e) {
      setAddMsg(e.response?.data?.detail || "Failed");
    }
  };

  const [forceLogoutMsg, setForceLogoutMsg] = useState("");

  const handleLogout = async () => {
    await axios.post(`${backendUrl}/api/auth/logout`, {}, getAuthConfig());
    sessionStorage.removeItem("pord_admin_token");
    navigate("/admin");
  };

  const handleForceLogoutAll = async () => {
    if (!window.confirm("This will sign out ALL users immediately. Continue?")) return;
    try {
      const { data } = await axios.post(`${backendUrl}/api/admin/force-logout-all`, {}, getAuthConfig());
      setForceLogoutMsg(data.message);
      setTimeout(() => setForceLogoutMsg(""), 5000);
    } catch (e) {
      setForceLogoutMsg(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="admin-container"><p className="body-text">Loading...</p></div>;

  return (
    <div className="admin-container" data-testid="admin-dashboard">
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
          <div>
            <h2 className="section-heading">Pord. Admin</h2>
            <p className="muted-caption">Subscribers, signups & analytics</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="ghost-link" onClick={handleForceLogoutAll} data-testid="force-logout-btn" style={{ display: "flex", alignItems: "center", gap: 6, color: "#e87c7c", border: "1px solid rgba(232,124,124,0.3)", borderRadius: 6, padding: "6px 14px", fontSize: 12, background: "rgba(232,124,124,0.06)" }}>
              <LogOut size={13} /> Force Sign Out All
            </button>
            <button className="ghost-link" onClick={handleLogout} data-testid="admin-logout-btn" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        {forceLogoutMsg && <p style={{ color: "#8ED973", fontSize: 13, marginBottom: 16 }}>{forceLogoutMsg}</p>}

        {error && <p style={{ color: "#e87c7c", marginBottom: 16 }}>{error}</p>}

        {/* Stats */}
        {stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 16, marginBottom: 32 }}>
            <StatCard label="Total Users" value={stats.total_users || 0} />
            <StatCard label="Active Paid" value={stats.active_paid || 0} color="#8ED973" />
            <StatCard label="Active Trial" value={stats.active_trial || 0} />
            <StatCard label="Manual" value={stats.active_manual || 0} />
            <StatCard label="Revenue" value={`$${stats.total_revenue || 0}`} color="#8ED973" />
            <StatCard label="Transactions" value={stats.paid_transactions || 0} />
          </div>
        )}

        {/* Tab buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          <TabBtn active={tab === "subscribers"} onClick={() => setTab("subscribers")} icon={<Users size={12} />} label="Subscribers" testId="tab-subscribers" />
          <TabBtn active={tab === "signups"} onClick={() => setTab("signups")} label="Signups" testId="tab-signups" />
          <TabBtn active={tab === "comments"} onClick={() => setTab("comments")} icon={<MessageSquare size={12} />} label="Comments" testId="tab-comments" />
          <TabBtn active={tab === "add"} onClick={() => setTab("add")} icon={<UserPlus size={12} />} label="Add Subscriber" testId="tab-add" />
          <TabBtn active={tab === "passcodes"} onClick={() => setTab("passcodes")} label="Passcodes" testId="tab-passcodes" />
          <TabBtn active={tab === "apikeys"} onClick={() => setTab("apikeys")} icon={<Key size={12} />} label="API Keys" testId="tab-apikeys" />
        </div>

        {/* Subscribers */}
        {tab === "subscribers" && (
          <div className="admin-card" style={{ overflowX: "auto" }}>
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Email</th><th>Status</th><th>Plan</th><th>Last Login</th><th>Expires</th><th></th></tr></thead>
              <tbody>
                {subscribers.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: 32 }}>No subscribers</td></tr>
                ) : subscribers.map((s) => (
                  <tr key={s._id}>
                    <td>{s.name || "—"}</td>
                    <td>{s.email}</td>
                    <td><StatusBadge sub={s.subscription} /></td>
                    <td>{s.subscription_plan || "—"}</td>
                    <td style={{ fontSize: 12 }}>{s.last_login ? new Date(s.last_login).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Never"}</td>
                    <td style={{ fontSize: 12 }}>{s.subscription?.expires ? new Date(s.subscription.expires).toLocaleDateString() : "∞"}</td>
                    <td>
                      {s.subscription?.active && (
                        <button className="ghost-link" onClick={() => handleDeactivate(s._id)} style={{ color: "#e87c7c", fontSize: 11 }}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Signups (legacy) */}
        {tab === "signups" && (
          <div className="admin-card" style={{ overflowX: "auto" }}>
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Email</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {signups.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: "center", padding: 32 }}>No signups</td></tr>
                ) : signups.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name || "—"}</td>
                    <td>{s.email || "—"}</td>
                    <td>{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                    <td><button className="ghost-link" onClick={() => handleDelete(s.id)} style={{ color: "#e87c7c" }}><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Comments */}
        {tab === "comments" && (
          <div className="admin-card" style={{ overflowX: "auto" }}>
            <table className="admin-table">
              <thead><tr><th>Comment</th><th>Email</th><th>Date</th></tr></thead>
              <tbody>
                {comments.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: "center", padding: 32 }}>No comments</td></tr>
                ) : comments.map((c) => (
                  <tr key={c.id}>
                    <td style={{ maxWidth: 400, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{c.comment}</td>
                    <td>{c.email || "—"}</td>
                    <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Subscriber */}
        {tab === "add" && (
          <div className="admin-card" style={{ padding: 24 }}>
            <p className="section-label" style={{ marginBottom: 16 }}>Add Manual Subscriber</p>
            <p className="body-text" style={{ marginBottom: 20, fontSize: 13, opacity: 0.7 }}>
              Grant access with adjustable duration. They'll receive an email to set their password.
            </p>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input data-testid="add-sub-email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="Email" style={inputStyle} />
              <input data-testid="add-sub-name" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Name" style={inputStyle} />
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input data-testid="add-sub-days" value={addDays} onChange={(e) => setAddDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="30" style={{ ...inputStyle, width: 60, textAlign: "center" }} />
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>days</span>
              </div>
              <button data-testid="add-sub-btn" className="cta-pill cta-pill-filled" onClick={handleAddSubscriber} disabled={!addEmail}
                style={{ padding: "10px 20px", fontSize: 13, opacity: addEmail ? 1 : 0.4 }}>
                Add
              </button>
            </div>
            {addMsg && <p data-testid="add-sub-msg" style={{ marginTop: 12, fontSize: 13, color: "var(--accent-teal)" }}>{addMsg}</p>}
            {addLink && (
              <div style={{ marginTop: 10, padding: "12px 16px", background: "rgba(184,212,208,0.06)", border: "1px solid rgba(184,212,208,0.15)", borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", whiteSpace: "nowrap" }}>Invite link:</span>
                <input readOnly value={addLink} style={{ flex: 1, background: "none", border: "none", color: "var(--accent-teal)", fontSize: 12, outline: "none", fontFamily: "monospace" }} onClick={(e) => e.target.select()} />
                <button onClick={() => { navigator.clipboard.writeText(addLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--accent-teal)", background: copied ? "var(--accent-teal)" : "transparent", color: copied ? "#1a1e14" : "var(--accent-teal)", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 200ms", whiteSpace: "nowrap" }}>
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Passcodes */}
        {tab === "passcodes" && (
          <div className="admin-card" style={{ padding: 24 }}>
            <p className="section-label" style={{ marginBottom: 16 }}>Guest Pass Passcodes</p>
            <p className="body-text" style={{ marginBottom: 20, fontSize: 13, opacity: 0.7 }}>
              Generate single-use passcodes. Users enter the code on the login page to get access.
            </p>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
              <input data-testid="new-passcode" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder="PASSCODE" style={{ ...inputStyle, textTransform: "uppercase", letterSpacing: 2, fontWeight: 600 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input value={newCodeDays} onChange={(e) => setNewCodeDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="14" style={{ ...inputStyle, width: 60, textAlign: "center" }} />
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>days</span>
              </div>
              <button data-testid="create-passcode-btn" className="cta-pill cta-pill-filled" disabled={!newCode.trim()}
                style={{ padding: "10px 20px", fontSize: 13, opacity: newCode.trim() ? 1 : 0.4 }}
                onClick={async () => {
                  try {
                    const { data } = await axios.post(`${backendUrl}/api/admin/passcodes`, { code: newCode.trim(), days: parseInt(newCodeDays) || 14 }, getAuthConfig());
                    setCodeMsg(data.message);
                    setNewCode(""); setNewCodeDays("14");
                    fetchData();
                    setTimeout(() => setCodeMsg(""), 5000);
                  } catch (e) { setCodeMsg(e.response?.data?.detail || "Failed"); }
                }}>
                Generate
              </button>
            </div>
            {codeMsg && <p style={{ marginBottom: 12, fontSize: 13, color: "var(--accent-teal)" }}>{codeMsg}</p>}
            <table className="admin-table">
              <thead><tr><th>Code</th><th>Duration</th><th>Status</th><th>Claimed By</th><th>Claimed At</th><th></th></tr></thead>
              <tbody>
                {passcodes.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 32 }}>No passcodes</td></tr>
                ) : passcodes.map((p) => (
                  <tr key={p._id}>
                    <td style={{ fontFamily: "monospace", letterSpacing: 2, fontWeight: 600 }}>{p.code}</td>
                    <td>{p.days} days</td>
                    <td><span style={{ color: p.status === "active" ? "#8ED973" : "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: 500 }}>{p.status}</span></td>
                    <td style={{ fontSize: 12 }}>{p.claimed_by || "—"}</td>
                    <td style={{ fontSize: 12 }}>{p.claimed_at ? new Date(p.claimed_at).toLocaleDateString() : "—"}</td>
                    <td>
                      <button className="ghost-link" style={{ color: "#e87c7c", fontSize: 11 }}
                        onClick={async () => { try { await axios.delete(`${backendUrl}/api/admin/passcodes/${p.code}`, getAuthConfig()); fetchData(); } catch {} }}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Passcode Users Activity */}
            <div style={{ marginTop: 32 }}>
              <p className="section-label" style={{ marginBottom: 12 }}>Passcode Users Activity</p>
              <table className="admin-table">
                <thead><tr><th>Code</th><th>Email</th><th>Last Login</th><th>Logins</th><th>Days Left</th><th>Status</th></tr></thead>
                <tbody>
                  {passcodeUsers.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: "center", padding: 32 }}>No passcode users yet</td></tr>
                  ) : passcodeUsers.map((u, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: "monospace", letterSpacing: 2, fontWeight: 600 }}>{u.passcode_code || "—"}</td>
                      <td style={{ fontSize: 12 }}>{u.email}</td>
                      <td style={{ fontSize: 12 }}>{u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}</td>
                      <td style={{ textAlign: "center" }}>{u.login_count || 0}</td>
                      <td style={{ textAlign: "center" }}>{u.days_left}</td>
                      <td><span style={{ color: u.status === "active" ? "#8ED973" : "#e87c7c", fontSize: 12, fontWeight: 500 }}>{u.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* API Keys */}
        {tab === "apikeys" && (
          <ApiKeysPanel backendUrl={backendUrl} getAuthConfig={getAuthConfig} />
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="admin-card" style={{ textAlign: "center" }}>
      <p className="section-label" style={{ marginBottom: 8 }}>{label}</p>
      <p className="section-heading" style={{ color: color || "var(--accent-teal)" }}>{value}</p>
    </div>
  );
}

function StatusBadge({ sub }) {
  if (!sub) return <span style={{ color: "rgba(255,255,255,0.3)" }}>—</span>;
  const colors = { paid: "#8ED973", trial: "#94DCF8", manual: "#DAF2D0", admin: "#DAF2D0" };
  const c = sub.active ? (colors[sub.reason] || "#8ED973") : "#e87c7c";
  return <span style={{ color: c, fontSize: 12, fontWeight: 500 }}>{sub.active ? sub.reason : "expired"}</span>;
}

function TabBtn({ active, onClick, icon, label, testId }) {
  return (
    <button data-testid={testId} className={`cta-pill ${active ? "cta-pill-filled" : ""}`} onClick={onClick} style={{ padding: "8px 20px", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: 4 }}>
      {icon} {label}
    </button>
  );
}

const inputStyle = {
  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 13, outline: "none", width: 180,
};
